
































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import {
  ImageStatus,
  JbicImage,
  JbicImageVersion
} from '@/store/modules/admin/types/admin.types';

// components
import BaseTable from '@/components/BaseTable.vue';
import BasePagination from '@/components/base/BasePagination.vue';
import BaseLoading from '@/components/base/BaseLoading.vue';
import { PaginationMixin } from '@/components/base/pagination.mixin';
import SortableTableHeader from '@/components/SortableTableHeader.vue';
import { Debounce } from '@/jbi-shared/util/debounce.vue-decorator';
import { ToastProgrammatic } from 'buefy';
import ImagesGrid from './ImagesGrid.vue';
import ImagesList from './ImagesList.vue';
import { checkJbicPermission } from '@/utils/rbac.util';
import { JbicPermissions } from '@/store/modules/roles-and-permissions/types/roles-and-permissions.types';

@Component({
  components: {
    BaseTable,
    BasePagination,
    BaseLoading,
    SortableTableHeader,
    ImagesGrid,
    ImagesList
  }
})
export default class Images extends mixins(PaginationMixin) {
  @Prop() public items!: JbicImage[];
  @Prop(Number) public currentPage!: number;
  @Prop(Number) public totalNumberOfPage!: number;
  @Prop(Number) public totalCount!: number;
  @Prop(Boolean) public isFirstPage!: boolean;
  @Prop(Boolean) public isLastPage!: boolean;
  @Prop(Number) public startItemIndex!: number;
  @Prop(Number) public endItemIndex!: number;
  @Prop() public imageStatus!: ImageStatus;

  public searchTerm: string = '';
  public displayType: string = 'grid';
  public selectedImageIds: number[] = [];
  public isSelectAllIndeterminate: boolean = false;
  public isSelectAll: boolean = false;
  public isDeselectAll: boolean = false;
  public isSelectAllChecked: boolean = false;
  public previousVersionImages: JbicImageVersion[] = [];

  get ImagesGrid() {
    return ImagesGrid;
  }

  get ImagesList() {
    return ImagesList;
  }

  get isActionMode() {
    return this.isSelectAllChecked || this.isSelectAllIndeterminate;
  }

  get imageIds(): number[] {
    return this.items.map((image) => image.id);
  }

  get groupId(): number {
    return this.items[0].groupId;
  }

  get isJbicImageCreateRole(): boolean {
    return checkJbicPermission(JbicPermissions.IMAGE_CREATE, this.groupId);
  }

  get isJbicImageUpdateRole(): boolean {
    return checkJbicPermission(JbicPermissions.IMAGE_UPDATE, this.groupId);
  }

  get isJbicImageDeleteRole(): boolean {
    return checkJbicPermission(JbicPermissions.IMAGE_DELETE, this.groupId);
  }

  get isJbicImageViewRole(): boolean {
    return checkJbicPermission(JbicPermissions.IMAGE_READ, this.groupId);
  }

  get isJbicImageManageRole(): boolean {
    return checkJbicPermission(JbicPermissions.IMAGE_MANAGE, this.groupId);
  }

  updateCheckbox() {
    // if any of the arrays are null or zero length, then isSelectAllIndeterminate === false
    if (
      !this.selectedImageIds ||
      !this.imageIds ||
      this.selectedImageIds.length === 0 ||
      this.imageIds.length === 0
    ) {
      this.isSelectAllChecked = false;
      this.isSelectAllIndeterminate = false;
      this.onInput(false);
      return;
    }

    // if the arrays are more than 0, and not the same length, then isSelectAllIndeterminate === false
    if (this.selectedImageIds.length !== this.imageIds.length) {
      this.isSelectAllIndeterminate = true;
      this.isSelectAllChecked = false;
      return;
    }

    // if all available, then isSelectAllIndeterminate === true
    const allImagesSame: boolean = this.imageIds.every((imageId) =>
      this.selectedImageIds.find(
        (selectedImageId) => selectedImageId === imageId
      )
    );
    if (allImagesSame) {
      this.isSelectAllIndeterminate = false;
      this.isSelectAllChecked = true;
      this.onInput(true);
      return;
    }

    const someImagesSame: boolean = this.imageIds.some((imageId) =>
      this.selectedImageIds.find(
        (selectedImageId) => selectedImageId === imageId
      )
    );
    if (someImagesSame) {
      this.isSelectAllChecked = false;
      this.isSelectAllIndeterminate = true;
      return;
    }
  }

  handleSelectImage(imageIds: number[]) {
    this.selectedImageIds = imageIds;
  }

  @Watch('searchTerm')
  @Debounce(500)
  onSearchTermChange(searchTerm: string) {
    this.$emit('searchTermChange', searchTerm);
  }

  @Watch('selectedImageIds')
  onSelectedImageIds() {
    this.updateCheckbox();
  }

  @Watch('imageStatus')
  onImageStatus() {
    // reset selectedImageIds on change of imageStatus
    this.selectedImageIds = [];
  }

  @Watch('items')
  onItemsChange() {
    // preserve selections for images that still exist after refresh
    const currentIds = new Set(this.items.map((img) => img.id));
    this.selectedImageIds = this.selectedImageIds.filter((id) =>
      currentIds.has(id)
    );
  }

  onInput(input: boolean) {
    this.isSelectAll = input;
    this.isDeselectAll = !input;
  }

  isImageStatus(status: ImageStatus): boolean {
    return this.imageStatus === status;
  }

  updateImageStatusSubmission(): void {
    if (!this.isJbicImageUpdateRole) {
      return;
    }

    // Return toast error to user
    const selectedImages = this.items.filter((img) =>
      this.selectedImageIds.includes(img.id)
    );

    const invalidImages = selectedImages.filter(
      (img) => !img.caption?.trim() || !img.alternateText?.trim()
    );

    if (invalidImages.length > 0) {
      ToastProgrammatic.open({
        message:
          'All selected images must have a caption and alternate text before submitting for approval.',
        type: 'is-danger',
        position: 'is-top'
      });
      return;
    }

    this.$parent?.$emit('updateImageStatusSubmission', this.selectedImageIds);
  }

  approveImage(): void {
    // only JBI Admin able to approve
    if (!this.isJbicImageManageRole) {
      return;
    }

    this.$parent?.$emit('approveImage', this.selectedImageIds);
  }

  declineImage(): void {
    // only JBI Admin able to decline
    if (!this.isJbicImageManageRole) {
      return;
    }

    this.$parent?.$emit('declineImage', this.selectedImageIds);
  }

  publishImage(): void {
    if (!this.isJbicImageUpdateRole) {
      return;
    }

    this.$parent?.$emit('publishImage', this.selectedImageIds);
  }

  unpublishImage(): void {
    if (!this.isJbicImageUpdateRole) {
      return;
    }

    this.$parent?.$emit('unpublishImage', this.selectedImageIds);
  }

  deleteImage(): void {
    if (!this.isJbicImageDeleteRole) {
      return;
    }

    this.$parent?.$emit('deleteImage', this.selectedImageIds);
  }
}
